import React, { useCallback, useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  Alert, 
  Button, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip
} from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';

const DeviceNeighborsView = () => {
  const { serial: routeSerial } = useParams();
  const [searchParams] = useSearchParams();
  const golden = searchParams.get('golden') === 'true';
  const [neighborsReport, setNeighborsReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch neighbors report for the specific device
  const fetchNeighborsReport = useCallback(async () => {
    if (!routeSerial) {
      setError('No device serial specified');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const serial = routeSerial.toLowerCase();
      const response = await api.getNeighborsReport(serial, golden);
      
      if (response && response.Neighbors && Array.isArray(response.Neighbors)) {
        setNeighborsReport(response);
      } else {
        setNeighborsReport({ Serial: serial, Neighbors: [], LastUpdated: new Date() });
      }
    } catch (err) {
      console.error('Error fetching neighbors report:', err);
      setError(err.message || 'Failed to fetch neighbors report');
      setNeighborsReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [routeSerial, golden]);

  // Initial fetch when component mounts
  useEffect(() => {
    if (routeSerial) {
      fetchNeighborsReport();
    }
  }, [routeSerial, fetchNeighborsReport]);

  // Helper to get band name - Band 1=2.4GHz, 2=5GHz, 3=6GHz
  const getBandName = (band) => {
    if (band === 1 || band === '1') return '2.4GHz';
    if (band === 2 || band === '2') return '5GHz';
    if (band === 3 || band === '3') return '6GHz';
    return `${band}GHz`;
  };

  // Helper to get signal quality color based on RSSI
  const getSignalColor = (rssi) => {
    if (rssi >= -50) return 'success';
    if (rssi >= -70) return 'warning';
    return 'error';
  };

  if (!routeSerial) {
    return (
      <Paper elevation={3} sx={{ p: 3, m: 2 }}>
        <Alert severity="error">
          No device serial specified in the URL.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Neighbors Scan Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Device: <strong>{routeSerial}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={fetchNeighborsReport}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && !neighborsReport && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading neighbors data...</Typography>
        </Box>
      )}

      {neighborsReport && (
        <>
          <Box sx={{ mb: 2 }}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>AP RSSI:</strong> How the neighbor hears this device (higher is better, in dBm)
                <br />
                <strong>STA RSSI:</strong> How this device hears the neighbor (higher is better, in dBm)
                <br />
                <strong>Good signal:</strong> ≥ -50 dBm (Green) • <strong>Fair:</strong> ≥ -70 dBm (Orange) • <strong>Poor:</strong> &lt; -70 dBm (Red)
              </Typography>
            </Alert>
          </Box>

          {neighborsReport.LastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Last updated: {new Date(neighborsReport.LastUpdated).toLocaleString()}
            </Typography>
          )}

          {neighborsReport.Neighbors && neighborsReport.Neighbors.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Neighbor Device</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Channel</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Band</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Frequency</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">AP RSSI</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">STA RSSI</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Hop Dist</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>BSSID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {neighborsReport.Neighbors.map((neighbor, index) => {
                    const apNodeId = neighbor.AP_NodeID || neighbor.ap_node_id || '';
                    const apSNR = neighbor.AP_SNR ?? neighbor.ap_snr ?? 0;
                    const apNF = neighbor.AP_NF ?? neighbor.ap_nf ?? 0;
                    const staSNR = neighbor.STA_SNR ?? neighbor.sta_snr ?? 0;
                    const staNF = neighbor.STA_NF ?? neighbor.sta_nf ?? 0;
                    const channel = neighbor.Channel ?? neighbor.channel ?? 0;
                    const band = neighbor.Band ?? neighbor.band ?? 0;
                    const frequency = neighbor.Freq ?? neighbor.freq ?? 0;
                    const hopDistance = neighbor.HOP_Distance ?? neighbor.hop_distance ?? 0;
                    const bssid = neighbor.AP_BSSID ?? neighbor.ap_bssid ?? '';
                    // Calculate RSSI values: RSSI = SNR + Noise Floor
                    const apRSSI = apSNR + apNF;
                    const staRSSI = staSNR + staNF;

                    return (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:hover': { backgroundColor: 'action.hover' },
                          '&:nth-of-type(odd)': { backgroundColor: 'action.selected' }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {apNodeId}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={channel} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={getBandName(band)} size="small" color="secondary" />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{frequency} MHz</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${apRSSI} dBm`} 
                            size="small" 
                            color={getSignalColor(apRSSI)}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${staRSSI} dBm`} 
                            size="small" 
                            color={getSignalColor(staRSSI)}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={hopDistance} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {bssid}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No neighbors found for this device. The device may not have performed a neighbors scan yet, or it may not detect any nearby devices.
            </Alert>
          )}

          {neighborsReport.Neighbors && neighborsReport.Neighbors.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Total neighbors detected: <strong>{neighborsReport.Neighbors.length}</strong>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(`/device/${routeSerial}`, '_blank')}
              >
                View Device Details
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default DeviceNeighborsView;
